(() => {
  if (window.GmlProductDesktop) {
    window.GmlProductDesktop.initialize();
    return;
  }

  const desktop = window.matchMedia('(min-width: 851px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Map();
  let sequence = 0;

  const rememberAttributes = (element, names) => names.map(name => [name, element.getAttribute(name)]);
  const restoreAttributes = (element, attributes) => attributes.forEach(([name, value]) => {
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  });
  const positionOf = element => element ? { element, parent: element.parentNode, next: element.nextSibling } : null;
  const restorePosition = position => {
    if (!position?.parent) return;
    position.parent.insertBefore(position.element, position.next?.parentNode === position.parent ? position.next : null);
  };
  const pixels = value => Number.parseFloat(value) || 0;

  const createController = root => {
    const info = root.querySelector('.gml-product__info');
    const identity = root.querySelector('.gml-product__identity');
    const purchase = root.querySelector('.gml-product__purchase');
    const accordions = root.querySelector('.gml-product__accordions');
    const specification = root.querySelector('.gml-product__accordion--specifications');
    const specificationBody = specification?.querySelector('.gml-product__description');
    const specs = root.querySelector('dl.gml-product__specs');
    const favorite = root.querySelector('.gml-product__save[data-wishlist-toggle]');
    if (!info || !identity || !accordions) return null;

    let enabled = false;
    let entries = [];
    let active = null;
    let positions = [];
    let observer = null;
    let frame = 0;
    let generation = 0;

    const stopAnimations = () => {
      const current = entries.map(entry => entry.details.open ? entry.body.getBoundingClientRect().height : 0);
      entries.forEach((entry, index) => {
        entry.animation?.cancel();
        entry.animation = null;
        entry.body.style.height = `${current[index]}px`;
      });
      return current;
    };

    const availableHeight = () => {
      const region = getComputedStyle(accordions);
      let remaining = accordions.clientHeight - pixels(region.paddingTop) - pixels(region.paddingBottom);
      let visible = 0;
      entries.forEach(entry => {
        const style = getComputedStyle(entry.details);
        if (style.display === 'none') return;
        visible += 1;
        const summaryStyle = getComputedStyle(entry.summary);
        remaining -= entry.summary.getBoundingClientRect().height
          + pixels(summaryStyle.marginTop) + pixels(summaryStyle.marginBottom)
          + pixels(style.borderTopWidth) + pixels(style.borderBottomWidth)
          + pixels(style.paddingTop) + pixels(style.paddingBottom)
          + pixels(style.marginTop) + pixels(style.marginBottom);
      });
      remaining -= Math.max(0, visible - 1) * pixels(region.rowGap);
      return Math.max(0, Math.floor(remaining));
    };

    const render = animate => {
      if (!enabled) return;
      const run = ++generation;
      const heights = stopAnimations();
      const available = availableHeight();
      const duration = animate && !reduced.matches ? 280 : 0;
      entries.forEach((entry, index) => {
        const opened = entry === active;
        const target = opened ? available : 0;
        const from = heights[index];
        if (opened || from > 0) entry.details.open = true;
        entry.body.style.overflowY = duration ? 'hidden' : 'auto';
        entry.body.style.height = `${target}px`;
        const finish = () => {
          if (!enabled || run !== generation) return;
          entry.animation = null;
          entry.body.style.height = `${target}px`;
          entry.body.style.overflowY = 'auto';
          entry.details.open = opened;
        };
        if (!duration || Math.abs(from - target) < 1 || typeof entry.body.animate !== 'function') {
          finish();
          return;
        }
        const animation = entry.body.animate(
          [{ height: `${from}px` }, { height: `${target}px` }],
          { duration, easing: 'cubic-bezier(.22,.61,.36,1)' }
        );
        entry.animation = animation;
        animation.finished.then(finish).catch(() => {});
      });
    };

    const schedule = () => {
      if (!enabled || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        render(false);
      });
    };

    const enable = () => {
      if (enabled) return;
      enabled = true;
      positions = [positionOf(favorite), positionOf(specs), positionOf(specification)];
      if (favorite) identity.append(favorite);
      if (specs && specificationBody) specificationBody.append(specs);
      const first = Array.from(accordions.children).find(element => element.tagName === 'DETAILS' && element !== specification);
      if (specification && first) first.after(specification);
      entries = Array.from(accordions.children).filter(element => element.tagName === 'DETAILS').map(details => {
        const summary = details.querySelector(':scope > summary');
        const body = details.querySelector(':scope > .gml-product__description');
        if (!summary || !body) return null;
        const entry = {
          details, summary, body, animation: null, originalOpen: details.open,
          summaryAttributes: rememberAttributes(summary, ['id', 'aria-controls']),
          bodyAttributes: rememberAttributes(body, ['id', 'role', 'aria-labelledby', 'tabindex', 'style'])
        };
        const id = ++sequence;
        if (!summary.id) summary.id = `gml-product-detail-heading-${id}`;
        if (!body.id) body.id = `gml-product-detail-panel-${id}`;
        summary.setAttribute('aria-controls', body.id);
        body.setAttribute('role', 'region');
        body.setAttribute('aria-labelledby', summary.id);
        body.setAttribute('tabindex', '0');
        body.style.boxSizing = 'border-box';
        body.style.minHeight = '0';
        body.style.paddingTop = '0';
        body.style.paddingBottom = '0';
        body.style.overflowX = 'hidden';
        body.style.overflowY = 'auto';
        body.style.overscrollBehaviorY = 'contain';
        body.style.scrollbarGutter = 'stable';
        entry.click = event => {
          if (!enabled || event.defaultPrevented || event.button > 0) return;
          if (event.target instanceof Element && event.target.closest('a, button, input, select, textarea')) return;
          event.preventDefault();
          active = active === entry ? null : entry;
          render(true);
        };
        summary.addEventListener('click', entry.click);
        return entry;
      }).filter(Boolean);
      active = entries.find(entry => entry.originalOpen) || null;
      entries.forEach(entry => {
        entry.body.style.height = '0px';
        entry.details.open = entry === active;
      });
      root.classList.add('gml-product--desktop-ready');
      render(false);
      if ('ResizeObserver' in window) {
        observer = new ResizeObserver(schedule);
        [accordions, info, identity, purchase, ...entries.map(entry => entry.summary)].filter(Boolean).forEach(element => observer.observe(element));
      }
      window.addEventListener('resize', schedule, { passive: true });
      document.fonts?.ready.then(schedule);
    };

    const disable = () => {
      if (!enabled) return;
      enabled = false;
      generation += 1;
      cancelAnimationFrame(frame);
      frame = 0;
      observer?.disconnect();
      observer = null;
      window.removeEventListener('resize', schedule);
      entries.forEach(entry => {
        entry.animation?.cancel();
        entry.summary.removeEventListener('click', entry.click);
        restoreAttributes(entry.summary, entry.summaryAttributes);
        restoreAttributes(entry.body, entry.bodyAttributes);
        entry.details.open = entry.originalOpen;
      });
      positions.forEach(restorePosition);
      root.classList.remove('gml-product--desktop-ready');
      entries = [];
      positions = [];
      active = null;
    };

    return {
      sync: () => desktop.matches ? enable() : disable(),
      resize: () => render(false),
      destroy: disable
    };
  };

  const initialize = () => {
    document.querySelectorAll('[data-gml-desktop-watch]').forEach(root => {
      if (controllers.has(root)) return;
      const controller = createController(root);
      if (!controller) return;
      controllers.set(root, controller);
      controller.sync();
    });
  };
  desktop.addEventListener('change', () => controllers.forEach(controller => controller.sync()));
  reduced.addEventListener('change', () => controllers.forEach(controller => controller.resize()));
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => controllers.forEach((controller, root) => {
    if (event.target instanceof Element && event.target.contains(root)) {
      controller.destroy();
      controllers.delete(root);
    }
  }));
  window.GmlProductDesktop = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
